import { useEffect, useRef, useState } from "react";

// Calls `save(payload)` after `delay` ms of inactivity since the latest payload.
// Saves never overlap: a save that comes due while another is in flight waits
// for it, so the server sees them in order (overlapping day-grid replaces used
// to trip over each other's deleted rows).
// A failed save keeps its payload (unless a newer one arrived), so flush() retries it.
// Returns { schedule(payload), saving, error, flush, cancel, hasPending }.
export default function useDebouncedSave(save, delay = 500) {
  const timerRef = useRef(null);
  const pendingRef = useRef(null);
  const inFlightRef = useRef(Promise.resolve());
  const failedRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const run = () => {
    inFlightRef.current = inFlightRef.current.then(async () => {
      const payload = pendingRef.current;
      pendingRef.current = null;
      if (payload === null || payload === undefined) return !failedRef.current;
      try {
        setSaving(true);
        setError("");
        await save(payload);
        failedRef.current = false;
        return true;
      } catch (e) {
        if (pendingRef.current === null) pendingRef.current = payload;
        failedRef.current = true;
        setError(e.message || "Save failed");
        return false;
      } finally {
        setSaving(false);
      }
    });
    return inFlightRef.current;
  };

  const schedule = (payload) => {
    pendingRef.current = payload;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(run, delay);
  };

  // Send anything pending now and wait until every save has finished. Resolves true when all saved.
  const flush = async () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    return run();
  };

  // Unsaved or failed changes waiting to go.
  const hasPending = () => pendingRef.current !== null || failedRef.current;

  // Drop anything pending (e.g. once the quote is locked).
  const cancel = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    pendingRef.current = null;
    failedRef.current = false;
  };

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return { schedule, saving, error, flush, cancel, hasPending };
}
